import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Loader2, Plus, Trash2, MessageCircle, X, BookOpen, Volume2, Square } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let sharedGuruAudio = null;
let activeVoiceReset = null;

const SUGGESTED_QUESTIONS = [
  "What does the Gita say about Karma?",
  "Who is Lord Shiva?",
  "Explain Dharma",
  "What is the meaning of Om?",
  "Krishna's teaching on fear",
  "What are the four Vedas?",
  "How to find inner peace?",
  "Tell me about Hanuman Ji",
];

export default function GuruChatPage({ user }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { fetchSessions(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const getToken = () => localStorage.getItem('dharmbook_token');

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/guru-chat/sessions`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSessions(res.data.sessions || []);
    } catch {}
  };

  const loadSession = async (sid) => {
    setLoadingHistory(true);
    setShowSidebar(false);
    try {
      const res = await axios.get(`${API}/guru-chat/history/${sid}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSessionId(sid);
      setMessages((res.data.messages || []).map(m => ({
        role: m.role,
        text: m.text,
        id: m.id,
      })));
    } catch { toast.error('Failed to load conversation'); }
    setLoadingHistory(false);
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setShowSidebar(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/guru-chat/session/${sid}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) startNewChat();
      toast.success('Conversation deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim(), id: Date.now().toString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/guru-chat/send`, {
        text: text.trim(),
        session_id: sessionId,
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: res.data.response,
        id: res.data.message_id,
      }]);
      fetchSessions();
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'DharmGuru is meditating. Please try again.';
      toast.error(errMsg);
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
    }
    setLoading(false);
  };

  return (
    <div className="h-[calc(100vh-3.5rem-5rem)] md:h-[calc(100vh-3.5rem)] flex flex-col bg-[#FFF9F0]" data-testid="guru-chat-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#8B2500] via-[#A0522D] to-[#8B4513] text-white flex-shrink-0 shadow-md z-10">
        <button onClick={() => navigate('/ai-granth')} className="p-1 rounded-full hover:bg-white/10" data-testid="guru-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-200 to-orange-300 flex items-center justify-center shadow-inner">
          <span className="text-xl leading-none" style={{ fontFamily: "'Yatra One', serif", color: '#8B2500' }}>&#x0950;</span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-[15px] font-bold leading-tight" style={{ fontFamily: "'Yatra One', cursive" }}>DharmGuru</h2>
          <p className="text-[11px] text-orange-200 leading-tight">Ask anything from the scriptures</p>
        </div>
        <button onClick={() => setShowSidebar(true)} className="p-2 rounded-full hover:bg-white/10" data-testid="guru-history-btn">
          <BookOpen className="w-5 h-5" />
        </button>
        <button onClick={startNewChat} className="p-2 rounded-full hover:bg-white/10" data-testid="guru-new-chat-btn">
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Sessions Sidebar */}
      {showSidebar && (
        <div className="fixed inset-0 z-50 flex" data-testid="guru-sidebar">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowSidebar(false)} />
          <div className="relative w-72 max-w-[80vw] bg-white shadow-2xl flex flex-col animate-in slide-in-from-left">
            <div className="px-4 py-3 bg-gradient-to-r from-[#8B2500] to-[#A0522D] text-white flex items-center justify-between">
              <h3 className="font-bold text-sm">Past Conversations</h3>
              <button onClick={() => setShowSidebar(false)}><X className="w-5 h-5" /></button>
            </div>
            <button onClick={startNewChat} className="mx-3 mt-3 mb-2 flex items-center gap-2 px-3 py-2.5 bg-orange-50 hover:bg-orange-100 rounded-xl text-sm font-semibold text-[#8B2500] transition-all" data-testid="sidebar-new-chat">
              <Plus className="w-4 h-4" /> New Conversation
            </button>
            <div className="flex-1 overflow-y-auto px-2 pb-4">
              {sessions.length === 0 ? (
                <p className="text-center text-sm text-slate-400 mt-8">No past conversations</p>
              ) : sessions.map(s => (
                <div
                  key={s.session_id}
                  onClick={() => loadSession(s.session_id)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl mb-1 text-left transition-all group cursor-pointer ${
                    sessionId === s.session_id ? 'bg-orange-100' : 'hover:bg-slate-50'
                  }`}
                  data-testid={`session-${s.session_id}`}
                >
                  <MessageCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="flex-1 text-sm text-slate-700 truncate">{s.last_message || 'New conversation'}</span>
                  <button onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded" data-testid={`delete-session-${s.session_id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-3 py-4" data-testid="guru-messages">
        {loadingHistory ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-[#8B2500] animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <EmptyState onSuggest={sendMessage} />
        ) : (
          <div className="max-w-2xl mx-auto space-y-4">
            {messages.map((msg) => (
              <ChatBubble key={msg.id} message={msg} />
            ))}
            {loading && (
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-200 to-orange-300 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <span className="text-sm" style={{ fontFamily: "'Yatra One', serif", color: '#8B2500' }}>&#x0950;</span>
                </div>
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-orange-100">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-[#8B2500] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#8B2500] rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
                    <span className="w-2 h-2 bg-[#8B2500] rounded-full animate-bounce" style={{ animationDelay: '400ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex-shrink-0 px-3 py-2 bg-white border-t border-orange-100" data-testid="guru-input-area">
        <div className="max-w-2xl mx-auto flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
            placeholder="Ask DharmGuru anything..."
            rows={1}
            className="flex-1 resize-none px-4 py-2.5 bg-[#FFF9F0] border border-orange-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-transparent placeholder:text-slate-400"
            style={{ maxHeight: '120px' }}
            data-testid="guru-input"
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B2500] to-[#A0522D] text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40 transition-all hover:shadow-md active:scale-95"
            data-testid="guru-send-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onSuggest }) {
  const shuffled = SUGGESTED_QUESTIONS.sort(() => 0.5 - Math.random()).slice(0, 4);
  return (
    <div className="flex flex-col items-center justify-center h-full px-4" data-testid="guru-empty-state">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-orange-200 flex items-center justify-center mb-4 shadow-lg">
        <span className="text-4xl" style={{ fontFamily: "'Yatra One', serif", color: '#8B2500' }}>&#x0950;</span>
      </div>
      <h2 className="text-lg font-bold text-[#8B2500] mb-1" style={{ fontFamily: "'Yatra One', cursive" }}>Namaste, Bhakt</h2>
      <p className="text-sm text-slate-500 text-center mb-6 max-w-xs">
        I am DharmGuru. Ask me anything about Hindu scriptures — Gita, Ramayana, Vedas, Upanishads, and more.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-md">
        {shuffled.map((q, i) => (
          <button
            key={i}
            onClick={() => onSuggest(q)}
            className="text-left px-4 py-3 bg-white rounded-xl border border-orange-100 text-sm text-slate-700 hover:bg-orange-50 hover:border-orange-200 transition-all shadow-sm"
            data-testid={`suggest-${i}`}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatBubble({ message }) {
  const isUser = message.role === 'user';
  const [voiceState, setVoiceState] = useState('idle'); // idle | loading | playing

  const toggleVoice = async () => {
    if (voiceState === 'playing') {
      if (sharedGuruAudio) sharedGuruAudio.pause();
      if (activeVoiceReset === setVoiceState) activeVoiceReset = null;
      setVoiceState('idle');
      return;
    }
    if (voiceState === 'loading') return;
    setVoiceState('loading');
    try {
      const res = await axios.post(
        `${API}/guru-chat/tts`,
        { text: message.text },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_token')}` },
          responseType: 'blob',
        }
      );
      if (sharedGuruAudio) sharedGuruAudio.pause();
      if (activeVoiceReset && activeVoiceReset !== setVoiceState) activeVoiceReset('idle');
      const url = URL.createObjectURL(res.data);
      sharedGuruAudio = new Audio(url);
      sharedGuruAudio.onended = () => {
        setVoiceState('idle');
        URL.revokeObjectURL(url);
        activeVoiceReset = null;
      };
      activeVoiceReset = setVoiceState;
      await sharedGuruAudio.play();
      setVoiceState('playing');
    } catch (e) {
      setVoiceState('idle');
      toast.error('Could not play voice. Please try again.');
    }
  };

  return (
    <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-200 to-orange-300 flex items-center justify-center flex-shrink-0 shadow-sm mt-0.5">
          <span className="text-sm" style={{ fontFamily: "'Yatra One', serif", color: '#8B2500' }}>&#x0950;</span>
        </div>
      )}
      <div
        className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed shadow-sm ${
          isUser
            ? 'bg-gradient-to-br from-[#8B2500] to-[#A0522D] text-white rounded-2xl rounded-tr-sm'
            : 'bg-white text-slate-800 rounded-2xl rounded-tl-sm border border-orange-50'
        }`}
        data-testid={`msg-${message.id}`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.text}</p>
        ) : (
          <>
            <FormattedResponse text={message.text} />
            <button
              onClick={toggleVoice}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-orange-50 text-[#8B2500] border border-orange-200 hover:bg-orange-100 transition-colors active:scale-95"
              data-testid={`guru-listen-btn-${message.id}`}
            >
              {voiceState === 'loading' ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing voice...</>
              ) : voiceState === 'playing' ? (
                <><Square className="w-3 h-3 fill-current" /> Stop</>
              ) : (
                <><Volume2 className="w-3.5 h-3.5" /> Listen</>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function FormattedResponse({ text }) {
  // Parse markdown-like formatting: **bold**, *italic*, line breaks
  const parts = text.split('\n').map((line, i) => {
    // Bold
    let formatted = line.replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-[#8B2500]">$1</strong>');
    // Italic
    formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');

    if (formatted.trim() === '') return <br key={i} />;
    return (
      <p
        key={i}
        className="mb-1 last:mb-0"
        dangerouslySetInnerHTML={{ __html: formatted }}
      />
    );
  });
  return <div className="guru-response">{parts}</div>;
}
